import { useState, useEffect } from "react";
import { RefreshCw, XCircle, CheckCircle, AlertCircle } from "lucide-react";
import { getSystemStats, getSystemHealth } from "./supportApi";

const statGroups = [
  {
    label: "Agents",
    cols: 2,
    keys: ["agents"],
    render: (status) => [
      { label: "Total Agents", value: status.agents?.total ?? 0 },
      { label: "Online Agents", value: status.agents?.online ?? 0 },
    ],
  },
  {
    label: "Tickets",
    cols: 5,
    render: (status) => [
      { label: "Total", value: status.tickets?.total ?? 0 },
      { label: "Open", value: status.tickets?.open ?? 0 },
      { label: "Pending", value: status.tickets?.pending ?? 0 },
      { label: "Resolved", value: status.tickets?.resolved ?? 0 },
      { label: "Closed", value: status.tickets?.closed ?? 0 },
    ],
  },
  {
    label: "Live Chats",
    cols: 5,
    render: (status) => [
      { label: "Total", value: status.chats?.total ?? 0 },
      { label: "Active", value: status.chats?.active ?? 0 },
      { label: "Pending", value: status.chats?.pending ?? 0 },
      { label: "Agent Handling", value: status.chats?.agent_handling ?? 0 },
      { label: "Closed", value: status.chats?.closed ?? 0 },
    ],
  },
  {
    label: "Content & Feedback",
    cols: 4,
    render: (status) => [
      { label: "Articles", value: status.articles?.total ?? 0 },
      { label: "Knowledge Base", value: status.documents?.total ?? 0 },
      { label: "Feedback", value: status.feedback?.total ?? 0 },
      { label: "Chat Ratings", value: status.chatRatings?.total ?? 0 },
    ],
  },
];

export function SystemStatus() {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [serviceHealth, setServiceHealth] = useState(null);
  const [isLoadingHealth, setIsLoadingHealth] = useState(false);
  const [lastHealthChecked, setLastHealthChecked] = useState(null);

  const checkSystemStatus = async () => {
    setIsLoading(true);
    try {
      const data = await getSystemStats();
      setStatus(data);
      setLastChecked(new Date());
    } catch (error) {
      console.error("Error checking system status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkServiceHealth = async (bypassCache = false) => {
    setIsLoadingHealth(true);
    try {
      const data = await getSystemHealth(bypassCache);
      setServiceHealth(data);
      setLastHealthChecked(new Date());
    } catch (error) {
      console.error("Error checking service health:", error);
      setServiceHealth(null);
    } finally {
      setIsLoadingHealth(false);
    }
  };

  useEffect(() => {
    checkSystemStatus();
    checkServiceHealth(false);
    const interval = setInterval(checkSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading && !status) {
    return (
      <div className="rounded-lg border border-border/70 bg-card p-6">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Checking system status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Service Health */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Service Health</h2>
          <button
            onClick={() => checkServiceHealth(true)}
            disabled={isLoadingHealth}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh service health"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoadingHealth ? "animate-spin" : ""}`} />
          </button>
        </div>
        {lastHealthChecked && (
          <p className="text-[10px] font-mono text-muted-foreground/60 mb-4">
            Last checked: {lastHealthChecked.toLocaleTimeString()}
          </p>
        )}

        {isLoadingHealth && !serviceHealth ? (
          <div className="flex items-center gap-2">
            <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Checking service health...</p>
          </div>
        ) : serviceHealth ? (
          <div className="space-y-3">
            {/* Overall status */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-md border ${
              serviceHealth.healthy
                ? "border-emerald-500/20 bg-emerald-500/[0.06]"
                : "border-red-500/20 bg-red-500/[0.06]"
            }`}>
              {serviceHealth.healthy ? (
                <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              )}
              <span className={`text-xs font-medium ${serviceHealth.healthy ? "text-emerald-400" : "text-red-400"}`}>
                {serviceHealth.healthy ? "All Systems Healthy" : "System Issues Detected"}
              </span>
              <span className="ml-auto text-[10px] font-mono text-muted-foreground/60">
                {new Date(serviceHealth.timestamp).toLocaleTimeString()}
              </span>
            </div>

            {/* Services grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(serviceHealth.services).map(([serviceName, serviceData]) => (
                <div
                  key={serviceName}
                  className={`rounded-md border p-3 ${
                    serviceData.healthy
                      ? "border-border/70 bg-white/[0.02]"
                      : "border-red-500/30 bg-red-500/[0.06]"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    {serviceData.healthy ? (
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    ) : (
                      <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                    )}
                    <span className="text-xs font-medium text-foreground capitalize">{serviceName}</span>
                  </div>
                  <p className={`text-[10px] font-medium ${serviceData.healthy ? "text-emerald-400" : "text-red-400"}`}>
                    {serviceData.healthy ? "Healthy" : "Failed"}
                  </p>
                  {serviceData.latency !== null && (
                    <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">
                      {serviceData.latency}ms
                    </p>
                  )}
                  {serviceData.error && (
                    <p className="text-[10px] text-red-400 mt-1.5 px-2 py-1 bg-red-500/10 rounded border border-red-500/20">
                      {serviceData.error}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-red-500/[0.06] border border-red-500/20 rounded-md">
            <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-400">Unable to load service health. Please try again.</p>
          </div>
        )}
      </div>

      {/* System Statistics */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">System Statistics</h2>
          <button
            onClick={checkSystemStatus}
            disabled={isLoading}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh statistics"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
        {lastChecked && (
          <p className="text-[10px] font-mono text-muted-foreground/60 mb-4">
            Last updated: {lastChecked.toLocaleTimeString()}
          </p>
        )}

        {status ? (
          <div className="space-y-5">
            {statGroups.map((group) => (
              <div key={group.label}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground/60 mb-2">
                  {group.label}
                </p>
                <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${group.cols}, minmax(0, 1fr))` }}>
                  {group.render(status).map((stat) => (
                    <div key={stat.label} className="rounded-md border border-border/70 bg-white/[0.02] p-3 text-center">
                      <p className="font-mono text-2xl text-foreground">{stat.value}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-red-500/[0.06] border border-red-500/20 rounded-md">
            <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-red-400">Unable to load statistics. Please try again later.</p>
          </div>
        )}
      </div>
    </div>
  );
}
